import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';

// Copy Button Component for Code Blocks
const CopyButton = ({ content, darkMode }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`
        absolute top-2 right-2 p-2 rounded-md text-xs font-medium
        transition-all duration-200 opacity-70 hover:opacity-100
        ${darkMode 
          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white' 
          : 'bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800'
        }
        ${copied ? 'scale-110' : 'hover:scale-105'}
      `}
      title={copied ? 'Copied!' : 'Copy code'}
    >
      {copied ? (
        <span className="text-green-500">✓ Copied</span>
      ) : (
        <span>Copy</span>
      )}
    </button>
  );
};

/**
 * TutorialContent - Personalized Article-Style Tutorial
 * 
 * This component renders tutorial content as a flowing article/blog post with:
 * - Real-time content updates based on user questions
 * - Beautiful markdown rendering with code highlighting
 * - Smooth update indicators when content changes
 * - Article-style flow rather than rigid steps
 */
const TutorialContent = ({
  tutorial = null,
  updatedSections = new Set(),
  onSectionComplete = () => {},
  darkMode = false,
  className = ""
}) => {
  const contentRef = useRef(null);
  const sectionRefs = useRef({});
  const [codeBlockViewModes, setCodeBlockViewModes] = useState({}); // Track view mode per code block

  // Security function to sanitize HTML using DOMPurify
  const sanitizeHtml = (html) => {
    return DOMPurify.sanitize(html, {
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
      ALLOW_DATA_ATTR: false
    });
  };

  // Extract HTML code blocks from markdown content
  const extractHtmlFromMarkdown = (content) => {
    // More flexible regex to handle different newline patterns
    const htmlCodeBlockRegex = /```html\s*([\s\S]*?)\s*```/g;
    const matches = [];
    let match;
    
    while ((match = htmlCodeBlockRegex.exec(content)) !== null) {
      matches.push(match[1].trim());
    }
    
    const result = matches.join('\n\n');
    console.log('🔍 [TutorialContent] HTML extraction:', {
      inputLength: content.length,
      matchesFound: matches.length,
      resultLength: result.length,
      firstMatch: matches[0]?.substring(0, 100) + '...'
    });
    
    return result;
  };

  // Scroll to updated sections
  useEffect(() => {
    if (updatedSections.size > 0) {
      const latestUpdatedSection = Array.from(updatedSections).pop();
      const sectionElement = sectionRefs.current[latestUpdatedSection];
      
      if (sectionElement) {
        sectionElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }
  }, [updatedSections]);



  if (!tutorial) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No tutorial data received...
        </div>
      </div>
    );
  }

  // Try different content field names
  const contentToRender = tutorial.tutorial_content || tutorial.content;

  // Debug logging to see what we're receiving
  console.log('🔍 [TutorialContent] Tutorial data:', {
    hasTutorial: !!tutorial,
    hasTutorialContent: !!tutorial?.tutorial_content,
    hasContent: !!tutorial?.content,
    tutorialKeys: tutorial ? Object.keys(tutorial) : 'none',
    tutorialContentLength: tutorial?.tutorial_content?.length || 0,
    contentLength: tutorial?.content?.length || 0,
    contentPreview: contentToRender?.substring(0, 200) + '...'
  });
  
  if (!contentToRender) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Tutorial data missing content...
          <br />
          <small>Available keys: {Object.keys(tutorial).join(', ')}</small>
          <br />
          <small>tutorial_content: {!!tutorial.tutorial_content ? 'exists' : 'missing'}</small>
          <br />
          <small>content: {!!tutorial.content ? 'exists' : 'missing'}</small>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={contentRef}
      className={`
        h-full overflow-y-auto overflow-x-hidden p-6 space-y-6
        scrollbar-thin scrollbar-track-transparent
        ${darkMode 
          ? 'scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-500' 
          : 'scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400'
        }
        ${className}
      `}
    >
        {/* Article Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className={`text-3xl font-bold mb-3 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            {tutorial.title}
          </h1>
          <p className={`text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {tutorial.description}
          </p>
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-4 text-sm">
            <span className={`px-3 py-1 rounded-full ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
              {tutorial.difficulty}
            </span>
            <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {tutorial.estimatedTime}
            </span>
            </div>
            

          </div>
        </motion.div>

        {/* Tutorial Content - Single Content Block */}
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
          className="relative"
        >
          {/* Tutorial Content */}
          {(() => {
            // Show tutorial text with individual code block toggles
            return (
                <div className={`
                  prose prose-lg max-w-none overflow-hidden
                  ${darkMode ? 'prose-invert' : ''}
                `}>
                  <style jsx>{`
                    .tutorial-content h1 {
                      font-size: 2rem;
                      font-weight: 600;
                      margin-top: 2rem;
                      margin-bottom: 1rem;
                      color: ${darkMode ? '#f3f4f6' : '#111827'};
                      border-bottom: 2px solid ${darkMode ? '#374151' : '#e5e7eb'};
                      padding-bottom: 0.5rem;
                    }
                    .tutorial-content h2 {
                      font-size: 1.5rem;
                      font-weight: 600;
                      margin-top: 2rem;
                      margin-bottom: 1rem;
                      color: ${darkMode ? '#f3f4f6' : '#111827'};
                      border-bottom: 1px solid ${darkMode ? '#374151' : '#e5e7eb'};
                      padding-bottom: 0.25rem;
                    }
                    .tutorial-content h3 {
                      font-size: 1.25rem;
                      font-weight: 600;
                      margin-top: 1.5rem;
                      margin-bottom: 0.75rem;
                      color: ${darkMode ? '#f3f4f6' : '#111827'};
                    }
                    .tutorial-content p {
                      margin-bottom: 1rem;
                      line-height: 1.7;
                      color: ${darkMode ? '#d1d5db' : '#374151'};
                    }
                    .tutorial-content strong {
                      font-weight: 600;
                      color: ${darkMode ? '#a855f7' : '#7c3aed'};
                    }
                    .tutorial-content ul, .tutorial-content ol {
                      margin-bottom: 1rem;
                      padding-left: 1.5rem;
                    }
                    .tutorial-content ul {
                      list-style-type: none;
                    }
                    .tutorial-content ul li {
                      position: relative;
                      margin-bottom: 0.5rem;
                      padding-left: 1rem;
                      color: ${darkMode ? '#d1d5db' : '#374151'};
                    }
                    .tutorial-content ul li::before {
                      content: "•";
                      color: ${darkMode ? '#a855f7' : '#7c3aed'};
                      font-weight: bold;
                      position: absolute;
                      left: 0;
                      top: 0;
                      font-size: 1.2em;
                    }
                    .tutorial-content ol li {
                      margin-bottom: 0.5rem;
                      color: ${darkMode ? '#d1d5db' : '#374151'};
                    }
                    .tutorial-content ol {
                      counter-reset: item;
                    }
                    .tutorial-content ol li {
                      position: relative;
                      padding-left: 1rem;
                    }
                    .tutorial-content ol li::before {
                      content: counter(item) ".";
                      counter-increment: item;
                      color: ${darkMode ? '#a855f7' : '#7c3aed'};
                      font-weight: bold;
                      position: absolute;
                      left: 0;
                      top: 0;
                    }
                    .tutorial-content code {
                      background-color: ${darkMode ? '#374151' : '#f3f4f6'};
                      color: ${darkMode ? '#a855f7' : '#7c3aed'};
                      padding: 0.125rem 0.375rem;
                      border-radius: 0.25rem;
                      font-size: 0.875rem;
                    }
                    .rendered-html-preview {
                      background-color: ${darkMode ? '#1f2937' : '#f9fafb'};
                      border: 2px solid ${darkMode ? '#7c3aed' : '#a855f7'};
                      border-radius: 0.5rem;
                      padding: 1.5rem;
                      margin: 1.5rem 0;
                      position: relative;
                    }
                    .rendered-html-preview::before {
                      content: "Preview";
                      position: absolute;
                      top: -12px;
                      left: 12px;
                      background: ${darkMode ? '#1f2937' : '#f9fafb'};
                      color: ${darkMode ? '#a855f7' : '#7c3aed'};
                      padding: 0 8px;
                      font-size: 0.75rem;
                      font-weight: 600;
                      text-transform: uppercase;
                      letter-spacing: 0.05em;
                    }
                  `}</style>
                  <div 
                    data-tutorial-content 
                    className="tutorial-content max-w-full overflow-hidden"
                  >
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        // Security: Block dangerous HTML tags in markdown content
                        script: () => null,
                        iframe: () => null,
                        object: () => null,
                        embed: () => null,
                        
                        // Hide HTML code blocks in preview mode, render them as HTML
                        pre: ({ children, ...props }) => {
                          const codeElement = children?.props?.children;
                          const codeString = typeof codeElement === 'string' ? codeElement : '';
                          const className = children?.props?.className || '';
                          
                          // If it's an HTML code block, render it with individual toggle
                          if (className.includes('language-html')) {
                            // Generate stable ID based on content hash
                            const blockId = `html-block-${codeString.slice(0, 50).replace(/[^a-zA-Z0-9]/g, '').slice(0, 20)}-${codeString.length}`;
                            const currentViewMode = codeBlockViewModes[blockId] || 'code';
                            
                            console.log('🔧 [CodeBlock] Block ID:', blockId, 'Current mode:', currentViewMode, 'All modes:', codeBlockViewModes);
                            
                            // Check HTML content type and visibility
                            const tempDiv = document.createElement('div');
                            tempDiv.innerHTML = codeString;
                            const hasTextContent = tempDiv.textContent.trim().length > 0;
                            const hasVisualElements = tempDiv.querySelector('img, svg, canvas, video, iframe, input, button, form, table');
                            
                            // Detect specific element types
                            const hasMetaTags = /<meta\s+[^>]*>/i.test(codeString);
                            const hasTitleTag = /<title[^>]*>.*?<\/title>/i.test(codeString);
                            const hasScriptTag = /<script[^>]*>[\s\S]*?<\/script>/i.test(codeString);
                            const hasLinkTags = /<link\s+[^>]*>/i.test(codeString);
                            const hasStructuredData = codeString.includes('application/ld+json') || codeString.includes('itemscope') || codeString.includes('property=');
                            const hasHeadElements = hasMetaTags || hasTitleTag || hasLinkTags;
                            
                            // Categorize content (order matters - structured data takes precedence over script)
                            const isLikelyVisible = hasTextContent || hasVisualElements;
                            const isStructuredData = hasStructuredData && !hasTextContent && !hasVisualElements;
                            const isHeadContent = hasHeadElements && !hasTextContent && !hasVisualElements && !hasStructuredData;
                            const isScriptContent = hasScriptTag && !hasTextContent && !hasVisualElements && !hasStructuredData;
                            
                            // Extract key information for preview
                            const getContentSummary = () => {
                              const elements = [];
                              
                              // Handle JSON-LD structured data
                              if (codeString.includes('application/ld+json')) {
                                try {
                                  const jsonMatch = codeString.match(/<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/i);
                                  if (jsonMatch) {
                                    const jsonData = JSON.parse(jsonMatch[1].trim());
                                    
                                    // Extract key schema properties
                                    if (jsonData['@type']) elements.push({ type: 'Schema Type', value: jsonData['@type'] });
                                    if (jsonData.name) elements.push({ type: 'Name', value: jsonData.name });
                                    if (jsonData.description) elements.push({ type: 'Description', value: jsonData.description.substring(0, 100) + (jsonData.description.length > 100 ? '...' : '') });
                                    if (jsonData.brand?.name) elements.push({ type: 'Brand', value: jsonData.brand.name });
                                    if (jsonData.review?.reviewRating?.ratingValue) elements.push({ type: 'Rating', value: jsonData.review.reviewRating.ratingValue + '/5' });
                                    if (jsonData.offers?.price) elements.push({ type: 'Price', value: '$' + jsonData.offers.price });
                                  }
                                } catch (e) {
                                  elements.push({ type: 'JSON-LD', value: 'Valid JSON-LD structured data' });
                                }
                              }
                              
                              if (hasTitleTag) {
                                const titleMatch = codeString.match(/<title[^>]*>(.*?)<\/title>/i);
                                if (titleMatch) elements.push({ type: 'Title', value: titleMatch[1].trim() });
                              }
                              
                              if (hasMetaTags) {
                                const metaMatches = codeString.match(/<meta\s+[^>]*>/gi) || [];
                                metaMatches.forEach(meta => {
                                  const nameMatch = meta.match(/name=["']([^"']+)["']/i);
                                  const propertyMatch = meta.match(/property=["']([^"']+)["']/i);
                                  const contentMatch = meta.match(/content=["']([^"']+)["']/i);
                                  
                                  if (nameMatch && contentMatch) {
                                    elements.push({ type: nameMatch[1], value: contentMatch[1] });
                                  } else if (propertyMatch && contentMatch) {
                                    elements.push({ type: propertyMatch[1], value: contentMatch[1] });
                                  }
                                });
                              }
                              
                              if (hasLinkTags) {
                                const linkMatches = codeString.match(/<link\s+[^>]*>/gi) || [];
                                linkMatches.forEach(link => {
                                  const relMatch = link.match(/rel=["']([^"']+)["']/i);
                                  const hrefMatch = link.match(/href=["']([^"']+)["']/i);
                                  if (relMatch) {
                                    elements.push({ type: `Link (${relMatch[1]})`, value: hrefMatch ? hrefMatch[1] : 'No href' });
                                  }
                                });
                              }
                              
                              return elements;
                            };
                            
                            return (
                              <div className="my-6 border rounded-lg overflow-hidden bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-purple-200 dark:border-purple-700">
                                {/* Header with toggle */}
                                <div className="flex items-center justify-between px-4 py-2 bg-white/50 dark:bg-gray-800/50 border-b border-purple-200 dark:border-purple-700">
                                  <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                                    {isStructuredData ? 'Structured Data' : 
                                     isHeadContent ? 'Head Elements' :
                                     isScriptContent ? 'Script Content' :
                                     'HTML Content'}
                                  </span>
                                  <div className={`flex rounded-md p-0.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                    <button
                                      onClick={() => setCodeBlockViewModes(prev => ({ ...prev, [blockId]: 'preview' }))}
                                      className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                                        currentViewMode === 'preview'
                                          ? darkMode 
                                            ? 'bg-purple-600 text-white shadow-sm' 
                                            : 'bg-purple-500 text-white shadow-sm'
                                          : darkMode
                                            ? 'text-gray-400 hover:text-gray-200'
                                            : 'text-gray-600 hover:text-gray-800'
                                      }`}
                                    >
                                      Preview
                                    </button>
                                    <button
                                      onClick={() => setCodeBlockViewModes(prev => ({ ...prev, [blockId]: 'code' }))}
                                      className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                                        currentViewMode === 'code'
                                          ? darkMode 
                                            ? 'bg-purple-600 text-white shadow-sm' 
                                            : 'bg-purple-500 text-white shadow-sm'
                                          : darkMode
                                            ? 'text-gray-400 hover:text-gray-200'
                                            : 'text-gray-600 hover:text-gray-800'
                                      }`}
                                    >
                                      Code
                                    </button>
                                  </div>
                                </div>
                                
                                {/* Content area */}
                                <div className="p-4">
                                  {currentViewMode === 'preview' ? (
                                    isLikelyVisible && !isScriptContent ? (
                                      <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(codeString) }} />
                                    ) : (
                                      <div className="py-4">
                                        {(isHeadContent || isStructuredData || isScriptContent) ? (
                                          <div className="space-y-3">
                                            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                              </svg>
                                              {isHeadContent ? 'Document Head Information:' :
                                               isStructuredData ? 'Structured Data Content:' :
                                               'Script Content:'}
                                            </div>
                                            
                                            {(isHeadContent || isStructuredData) && (
                                              <div className="space-y-2">
                                                {getContentSummary().map((item, idx) => (
                                                  <div key={idx} className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                                                    <div className="flex justify-between items-start gap-3">
                                                      <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">
                                                        {item.type}
                                                      </span>
                                                      <div className="flex-1 text-sm text-gray-700 dark:text-gray-300 break-words">
                                                        {item.value}
                                                      </div>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                            
                                            {isScriptContent && (
                                              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                                                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
                                                  </svg>
                                                  JavaScript code (executes in browser)
                                                </div>
                                              </div>
                                            )}
                                            
                                            <div className="text-xs text-gray-500 dark:text-gray-400 italic text-center pt-2">
                                              Switch to "Code" view to see the complete markup
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="text-center py-8 text-gray-500 dark:text-gray-400 italic">
                                            <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.05 7.05M14.12 14.12l1.828 1.829m-5.657-5.657L8.464 8.464"/>
                                            </svg>
                                            HTML contains invisible elements
                                            <br />
                                            <small className="text-xs">Switch to "Code" to view the markup</small>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  ) : (
                                    <div className="relative group">
                                      <pre className={`
                                        language-html font-mono text-sm leading-relaxed
                                        max-w-full overflow-x-auto p-3 rounded
                                        ${darkMode ? 'bg-gray-900 text-gray-200 border-gray-700' : 'bg-gray-50 text-gray-800 border-gray-200'}
                                        border
                                        overflow-hidden
                                      `}>
                                        <code className="whitespace-pre-wrap break-words break-all max-w-full overflow-hidden">
                                          {codeString}
                                        </code>
                                      </pre>
                                      <CopyButton content={codeString} darkMode={darkMode} />
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          
                          // Regular code blocks get copy button
                          return (
                            <div className="relative group">
                              <pre {...props} className={`tutorial-pre font-mono text-sm leading-relaxed max-w-full overflow-x-auto p-3 rounded border overflow-hidden ${darkMode ? 'bg-gray-900 text-gray-200 border-gray-700' : 'bg-gray-50 text-gray-800 border-gray-200'}`}>
                                <code className="whitespace-pre-wrap break-words break-all max-w-full overflow-hidden">
                                  {codeString}
                                </code>
                              </pre>
                              {codeString && (
                                <CopyButton content={codeString.trim()} darkMode={darkMode} />
                              )}
                            </div>
                          );
                        },
                        // Custom blockquote renderer
                        blockquote: ({ children, ...props }) => (
                          <blockquote 
                            {...props}
                            className={`
                              border-l-4 pl-4 py-2 my-4 italic
                              ${darkMode 
                                ? 'border-purple-500 bg-purple-900/20 text-purple-200' 
                                : 'border-purple-500 bg-purple-50 text-purple-800'
                              }
                            `}
                          >
                            {children}
                          </blockquote>
                        ),
                        
                        // Custom link renderer - open external links in new tab
                        a: ({ href, children, ...props }) => {
                          const isExternal = href && (href.startsWith('http://') || href.startsWith('https://'));
                          return (
                            <a
                              {...props}
                              href={href}
                              target={isExternal ? '_blank' : undefined}
                              rel={isExternal ? 'noopener noreferrer' : undefined}
                              className={`
                                underline transition-colors
                                ${darkMode 
                                  ? 'text-blue-400 hover:text-blue-300' 
                                  : 'text-blue-600 hover:text-blue-500'
                                }
                              `}
                            >
                              {children}
                              {isExternal && (
                                <svg 
                                  className="inline w-3 h-3 ml-1" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
                                  />
                                </svg>
                              )}
                            </a>
                          );
                        }
                      }}
                    >
                      {contentToRender}
                    </ReactMarkdown>
                  </div>
                </div>
              );
          })()}
              </motion.article>



    </div>
  );
};

export default TutorialContent;
